<script setup>
import {ref, computed, nextTick, defineProps} from "vue"
import Text from "@/components/common/Text.vue";
import {useI18n} from "vue-i18n";

const code = ref(["", "", "", "", "", ""]);
const inputs = ref([]);
const errorMessage = ref('');
const remember = ref(false);
const emit = defineEmits(["submit"])
const { t } = useI18n();

const handleConfirmCode = () => {
    emit("submit", {
        code: code.value.join(""),
        remember: remember.value,
    })
}

const handleInput = (event, index) => {
    const value = event.target.value.replace(/[^0-9]/g, "").slice(0, 1);
    code.value[index] = value;

    if (value && index < code.value.length - 1) {
        nextTick(() => inputs.value[index + 1].focus());
    }
}

const props = defineProps({
    login: String,
    // Qual metodo esta em uso, dito pela propria recusa do login. O padrao e
    // `app` para a API antiga, que nao manda esse campo.
    metodo: { type: String, default: 'app' },
    // O e-mail mascarado, quando o metodo e `email`.
    destino: { type: String, default: '' },
});

/**
 * O texto muda com o metodo, e isso nao e cosmetico: mandar quem usa codigo
 * por e-mail abrir um aplicativo autenticador que nao tem deixa a pessoa
 * parada numa tela sem saida aparente.
 */
const titulo = computed(() => {
    if (props.metodo === 'email') {
        return props.destino
            ? t('components.authentication.twoFactorCode.codigoEnviadoPara', { destino: props.destino })
            : t('components.authentication.twoFactorCode.codigoEnviado');
    }

    return t('components.authentication.twoFactorCode.openAuthApp');
});

const rotulo = computed(() =>
    props.metodo === 'email'
        ? t('components.authentication.twoFactorCode.enterEmailCode')
        : t('components.authentication.twoFactorCode.enterAppCode')
);

/**
 * Colar o codigo inteiro num campo so.
 *
 * Sem isto, colar "252829" deixava o primeiro campo com "2" e os outros
 * vazios: o `maxlength=1` corta, e o `handleInput` ainda faz `slice(0, 1)`.
 * Passou despercebido enquanto o segundo fator era so aplicativo, onde a
 * pessoa le do celular e digita -- codigo que chega por e-mail ela copia, e
 * colar e o gesto natural.
 *
 * Distribui a partir do campo onde colou, entao colar no primeiro preenche
 * tudo e colar no meio completa o resto. Descarta o que nao for digito, para
 * o codigo copiado com espaco ou quebra de linha junto funcionar igual.
 */
const handlePaste = (event, index) => {
    const texto = (event.clipboardData ?? window.clipboardData)?.getData('text') ?? '';
    const digitos = texto.replace(/\D/g, '');

    if (!digitos) {
        return;
    }

    event.preventDefault();

    const disponiveis = code.value.length - index;
    const recorte = digitos.slice(0, disponiveis).split('');

    recorte.forEach((digito, i) => {
        code.value[index + i] = digito;
    });

    // Foca o ultimo campo preenchido, e nao o seguinte: se o codigo completou,
    // nao ha seguinte, e o cursor ficaria num campo inexistente.
    const ultimo = Math.min(index + recorte.length - 1, code.value.length - 1);
    nextTick(() => inputs.value[ultimo]?.focus());
}

const handleBackspace = (event, index) => {
    if (event.key === "Backspace" && !code.value[index] && index > 0) {
        nextTick(() => inputs.value[index - 1].focus());
    }
}

</script>

<template>
    <div class="flex gap-2 justify-center">
        <form  @submit.prevent="handleConfirmCode">
            <Text class="text-center mb-5" tag="h5" weight="medium">
                {{ titulo }}
            </Text>
            <v-label class="fw-medium w-100 text-black mt-5">
                {{ rotulo }}
            </v-label>
            <div class="d-inline-flex justify-center mt-4">
                <v-text-field
                    v-for="(digit, index) in code"
                    :key="index"
                    v-model="code[index]"
                    variant="outlined"
                    density="comfortable"
                    class="w-33 align-center me-2 centered-input text-black"
                    maxlength="1"
                    hide-details
                    inputmode="numeric"
                    pattern="[0-9]*"
                    ref="inputs"
                    @input="handleInput($event, index)"
                    @keydown="handleBackspace($event, index)"
                    @paste="handlePaste($event, index)"
                    autocomplete="off"
                />
            </div>
            <v-checkbox v-model="remember">
                <template v-slot:label>
                    <span class="text-black"> {{ t('components.authentication.twoFactorCode.rememberDeviceSecure') }} </span>
                </template>
            </v-checkbox>
            <v-btn type="submit" class="two-factor-code__submit w-100">
                {{ t('common.labels.confirm') }}
            </v-btn>
        </form>
    </div>
    <slot name="extra"></slot>
</template>
<style scoped>
.centered-input {
    max-width: 48px;
}

.centered-input :deep(input) {
    text-align: center;
    font-weight: 700;
}

.centered-input :deep(.v-field) {
    border-radius: 10px;
    background: #f8f9ff;
}

.two-factor-code__submit {
    min-height: 48px;
    border-radius: 10px;
    background: linear-gradient(135deg, #3af334 0%, rgb(70 221 52 / 61%) 100%);
    box-shadow: 0 6px 24px rgba(55, 216, 10, 0.28);
    color: rgba(44, 44, 44, 0.81) !important;
    font-family: Inter, sans-serif;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
    transition: box-shadow 0.2s, transform 0.2s, opacity 0.2s;
}

.two-factor-code__submit:hover {
    box-shadow: 0 10px 28px rgba(55, 216, 10, 0.34);
    transform: translateY(-1px);
}
</style>
